<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Decimal from 'decimal.js'
import {
  DateInput,
  DateOnly,
  DecimalInput,
  InstantInput,
  RemoveButton,
  TextInput,
  ToggleSwitch,
} from '@hantera/design-system'
import { useLocalization } from '@hantera/portal-app'
import { defaultForKind, type ParamKind } from './paramTypes'

const props = defineProps<{
  kind: ParamKind
  modelValue: unknown
  disabled?: boolean
  inputId?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: unknown): void
}>()

const t = useLocalization('jobs')

const textValue = computed({
  get: () => (typeof props.modelValue === 'string' ? props.modelValue : ''),
  set: (v: string) => emit('update:modelValue', v),
})

// Local ref with stable identity so DecimalInput's internal tracking stays in sync.
// Bridging via a writable computed produced fresh Decimal(0) instances on every read,
// which caused DecimalInput to revert to 0 on blur.
//
// decimal.js may be duplicated across the design-system and portal workspaces, so the
// Decimal class DecimalInput emits can differ from the one in this file. Normalize on
// the way in/out so values[name] consistently holds an instance of the local Decimal class.
function toLocalDecimal(v: unknown): Decimal | undefined {
  if (v === undefined || v === null) return undefined
  if (v instanceof Decimal) return v
  const candidate = v as { toString?: () => string }
  if (typeof candidate?.toString === 'function') {
    try {
      return new Decimal(candidate.toString())
    } catch {
      return undefined
    }
  }
  return undefined
}

const numberValue = ref<Decimal | undefined>(toLocalDecimal(props.modelValue))

watch(
  () => props.modelValue,
  (val) => {
    const next = toLocalDecimal(val)
    if (next === undefined && numberValue.value === undefined) return
    if (next !== undefined && numberValue.value !== undefined && numberValue.value.equals(next)) return
    numberValue.value = next
  },
)

watch(numberValue, (val) => {
  const normalized = toLocalDecimal(val)
  if (val !== normalized) {
    // Re-enter with the canonical Decimal class
    numberValue.value = normalized
    return
  }
  const current = toLocalDecimal(props.modelValue)
  if (normalized === undefined && current === undefined) return
  if (normalized !== undefined && current !== undefined && current.equals(normalized)) return
  emit('update:modelValue', normalized)
})

const booleanValue = computed({
  get: () => props.modelValue === true,
  set: (v: boolean) => emit('update:modelValue', v),
})

const instantValue = computed({
  get: () => (props.modelValue instanceof Date ? props.modelValue : undefined),
  set: (v: Date | undefined) => emit('update:modelValue', v),
})

const dateValue = computed({
  get: () => (props.modelValue instanceof DateOnly ? props.modelValue : undefined),
  set: (v: DateOnly | undefined) => emit('update:modelValue', v),
})

const jsonValue = computed({
  get: () => (typeof props.modelValue === 'string' ? props.modelValue : ''),
  set: (v: string) => emit('update:modelValue', v),
})

const items = computed<unknown[]>(() =>
  Array.isArray(props.modelValue) ? props.modelValue : [],
)

function updateItem(index: number, value: unknown): void {
  const next = items.value.slice()
  next[index] = value
  emit('update:modelValue', next)
}

function addItem(): void {
  if (props.kind.kind !== 'list') return
  const next = items.value.slice()
  next.push(defaultForKind(props.kind.itemKind))
  emit('update:modelValue', next)
}

function removeItem(index: number): void {
  const next = items.value.slice()
  next.splice(index, 1)
  emit('update:modelValue', next)
}
</script>

<template>
  <TextInput
    v-if="kind.kind === 'text'"
    v-model="textValue"
    :disabled="disabled"
  />

  <DecimalInput
    v-else-if="kind.kind === 'number'"
    v-model="numberValue"
    :allow-empty="true"
    :disabled="disabled"
  />

  <label v-else-if="kind.kind === 'boolean'" class="boolean-field">
    <ToggleSwitch v-model="booleanValue" :disabled="disabled" />
    <span class="boolean-label">{{ booleanValue ? 'true' : 'false' }}</span>
  </label>

  <InstantInput
    v-else-if="kind.kind === 'instant'"
    v-model="instantValue"
  />

  <DateInput
    v-else-if="kind.kind === 'date'"
    v-model="dateValue"
  />

  <div v-else-if="kind.kind === 'list'" class="list-field">
    <div v-if="items.length === 0" class="list-empty">
      {{ t('detail.scheduleDialog.listEmpty') }}
    </div>
    <div
      v-for="(item, index) in items"
      :key="index"
      class="list-item"
    >
      <div class="list-item-input">
        <ParamInput
          :kind="kind.itemKind"
          :model-value="item"
          :disabled="disabled"
          @update:model-value="(v) => updateItem(index, v)"
        />
      </div>
      <button
        type="button"
        class="btn-silent list-remove"
        :disabled="disabled"
        :title="t('detail.scheduleDialog.removeItem')"
        @click="removeItem(index)"
      >
        <RemoveButton />
      </button>
    </div>
    <button
      type="button"
      class="btn-secondary list-add"
      :disabled="disabled"
      @click="addItem"
    >
      + {{ t('detail.scheduleDialog.addItem') }}
    </button>
  </div>

  <textarea
    v-else
    :id="inputId"
    v-model="jsonValue"
    class="json-textarea monospace"
    :placeholder="t('detail.scheduleDialog.jsonPlaceholder')"
    :disabled="disabled"
    spellcheck="false"
    rows="3"
  />
</template>

<style scoped>
.monospace {
  font-family: monospace;
}

.boolean-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
}

.boolean-label {
  font-family: monospace;
  color: hsl(var(--color-30));
}

.list-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.4rem;
  background: hsl(var(--color-97));
  border: 1px solid hsl(var(--color-88));
  border-radius: 4px;
}

.list-empty {
  font-size: 12px;
  color: hsl(var(--color-50));
  font-style: italic;
  padding: 0.25rem 0.1rem;
}

.list-item {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.4rem;
}

.list-item-input {
  min-width: 0;
}

.list-remove {
  padding: 0;
  height: 24px;
  width: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.list-remove :deep(.remove-button) {
  padding: 0;
}

.list-remove :deep(svg) {
  width: 12px;
  height: 12px;
}

.list-add {
  align-self: flex-start;
  font-size: 12px;
  padding: 0.25rem 0.6rem;
}

.json-textarea {
  width: 100%;
  padding: 0.4rem 0.5rem;
  font-size: 13px;
  border: 1px solid hsl(var(--color-80));
  border-radius: 3px;
  background: hsl(var(--color-98));
  box-sizing: border-box;
  resize: vertical;
  min-height: 60px;
}

.json-textarea:focus {
  outline: none;
  border-color: hsl(var(--color-hue-blue), 71%, 45%);
}

.json-textarea:disabled {
  background: hsl(var(--color-95));
  cursor: not-allowed;
}
</style>
